/**
 * AdminCMS.jsx — two admin pages bundled together:
 *   1. <AdminProfileDna />        — edit patron DNA fields (label, options, required)
 *   2. <AdminMenuPermissions />   — pick which menus PM/Researcher can see
 */
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import api from "../lib/api";
import { getCachedSiteContent } from "../lib/useSiteContent";
import MarkdownEditor from "../components/MarkdownEditor";

// =====================================================================
// (1) Profile DNA Editor
// =====================================================================
export function AdminProfileDna() {
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/profile-dna").then((r) => setFields(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.key || !editing.label) return toast.error("Key and label required");
    setSaving(true);
    try {
      const optsArr = (editing.options || "")
        .toString()
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        ...editing,
        options: optsArr,
        order: Number(editing.order) || 999,
        is_core: !!editing.is_core,
        tooltip: editing.tooltip || "",
      };
      await api.put(`/admin/profile-dna/${editing.key}`, payload);
      toast.success(adding ? "Field created." : "Field updated.");
      setEditing(null); setAdding(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (key) => {
    const f = fields.find((x) => x.key === key);
    if (f && f.is_core) {
      toast.error("Core identity fields cannot be deleted — they're part of the patron form's hardcoded Identity section. You can hide them instead.");
      return;
    }
    if (!window.confirm(`Delete the field "${key}" permanently? Existing patron data won't be wiped, but the field disappears from the form.`)) return;
    try { await api.delete(`/admin/profile-dna/${key}`); load(); toast.success("Deleted."); }
    catch (err) { toast.error("Delete failed"); }
  };

  const startAdd = () => {
    setEditing({ key: "", label: "", section: "Custom", kind: "pill_single",
                 options: "Option 1\nOption 2", required: true, hidden: false, order: 999, is_lifestyle: true, tooltip: "" });
    setAdding(true);
  };
  const startEdit = (f) => {
    setEditing({ ...f, options: (f.options || []).join("\n") });
    setAdding(false);
  };

  const sections = [...new Set(fields.map((f) => f.section))];

  return (
    <div className="max-w-5xl" data-testid="admin-profile-dna">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · PROFILE CMS</div>
        <h1 className="text-4xl font-black tracking-tighter">Patron Profile DNA</h1>
        <p className="text-sm text-neutral-500 mt-1">Edit the dropdown options, labels, and required status of every patron profile field. Add new fields as your research needs evolve.</p>
      </div>

      <Button onClick={startAdd} className="bg-mango hover:bg-mango/90 text-white rounded-none mb-6" data-testid="add-dna">+ Add new field</Button>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-6 text-xs text-blue-900 max-w-3xl" data-testid="dna-core-help">
        <strong>About core identity fields:</strong> Rows tagged <span className="font-bold bg-blue-200 px-1.5 py-0.5 mx-1">CORE</span> (Country, City, Region, Gender, Age band, SEC, etc.) are rendered by the patron form&apos;s hardcoded Identity section. You can rename their label, edit their options/required flag, or hide them from the form — but they can&apos;t be deleted because the form depends on them for survey targeting & weighting.
      </div>

      <div className="space-y-6">
        {sections.map((sec) => (
          <div key={sec}>
            <div className="overline mb-3">{sec}</div>
            <div className="space-y-1">
              {fields.filter((f) => f.section === sec).sort((a, b) => a.order - b.order).map((f) => (
                <div key={f.key} className="bg-white border border-neutral-200 p-3 flex items-center justify-between gap-3 text-sm" data-testid={`dna-row-${f.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold flex items-center gap-2 flex-wrap">
                      {f.is_core && <span className="bg-blue-600 text-white text-[10px] font-mono px-1.5 py-0.5 tracking-wider" data-testid={`dna-core-badge-${f.key}`}>CORE</span>}
                      {f.hidden && <span className="bg-neutral-700 text-white text-[10px] font-mono px-1.5 py-0.5 tracking-wider">HIDDEN</span>}
                      <span>{f.label}</span>
                      {f.required && <span className="text-red-600">*</span>}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      <code className="bg-neutral-100 px-1">{f.key}</code> · {f.kind} · {(f.options || []).length} option{(f.options || []).length === 1 ? "" : "s"}
                    </div>
                    {(f.options || []).length > 0 && (
                      <div className="text-xs text-neutral-600 mt-1 truncate">{(f.options || []).join(" · ")}</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEdit(f)} className="text-blue-600 hover:underline text-xs" data-testid={`edit-dna-${f.key}`}>Edit</button>
                    {!f.is_core && (
                      <button onClick={() => del(f.key)} className="text-red-600 hover:underline text-xs" data-testid={`del-dna-${f.key}`}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setAdding(false); }} data-testid="dna-modal">
          <div className="bg-white max-w-2xl w-full p-6 border-2 border-black max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">{adding ? "ADD FIELD" : "EDIT FIELD"}</div>
            {editing.is_core && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4 text-xs text-blue-900" data-testid="dna-core-edit-notice">
                <strong>This is a CORE identity field.</strong> Its <code>key</code>, <code>kind</code> and <code>section</code> are locked because the patron form renders it from a hardcoded Identity block. You can still rename the label, edit the options (if applicable), toggle required, or hide it from the form.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label>Key (machine name — letters_and_underscores, no spaces)</Label>
                <Input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                       disabled={!adding || editing.is_core} className="rounded-none font-mono" data-testid="dna-key" />
              </div>
              <div>
                <Label>Label (shown to patron)</Label>
                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="rounded-none" data-testid="dna-label" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Section</Label>
                  <Input value={editing.section} disabled={editing.is_core} onChange={(e) => setEditing({ ...editing, section: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="rounded-none" />
                </div>
              </div>
              <div>
                <Label>Field type</Label>
                <select value={editing.kind} disabled={editing.is_core} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="w-full border border-neutral-300 p-2 rounded-none disabled:bg-neutral-100" data-testid="dna-kind">
                  <option value="pill_single">Pill — single select</option>
                  <option value="pill_multi">Pill — multi-select</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="number">Number</option>
                  <option value="text">Free text</option>
                  <option value="cnic">CNIC (auto-masked xxxxx-xxxxxxx-x)</option>
                  <option value="core">Core (hardcoded Identity field)</option>
                </select>
              </div>
              {(["pill_single", "pill_multi", "dropdown", "core"].includes(editing.kind)) && (
                <div>
                  <Label>Options (one per line)</Label>
                  <textarea value={editing.options} onChange={(e) => setEditing({ ...editing, options: e.target.value })} rows={6} className="w-full border border-neutral-300 p-2 font-mono text-xs" data-testid="dna-options" />
                  {editing.is_core && editing.key === "city" && <p className="text-[11px] text-neutral-500 mt-1">City list is sourced from the City selector component — options here are ignored.</p>}
                  {editing.is_core && editing.key === "country_of_residence" && <p className="text-[11px] text-neutral-500 mt-1">Country list is sourced from the Country selector component — options here are ignored.</p>}
                </div>
              )}
              <div>
                <Label>Tooltip <span className="text-neutral-500 font-normal text-xs">(optional — leave blank to hide the (?) icon next to this field on the patron form)</span></Label>
                <textarea
                  value={editing.tooltip || ""}
                  onChange={(e) => setEditing({ ...editing, tooltip: e.target.value })}
                  rows={3}
                  placeholder="e.g. Used only to verify you're a real Pakistani resident. Stored encrypted. Never sold or shown to clients."
                  className="w-full border border-neutral-300 p-2 text-sm"
                  data-testid="dna-tooltip"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Patrons see a small <strong>(?)</strong> icon next to the field label. Hover or tap it to reveal this explanation. Use it to soften sensitive questions (income, CNIC) and explain how the data is used.</p>
              </div>
              <div className="flex gap-4 items-center flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={editing.required} onCheckedChange={(v) => setEditing({ ...editing, required: v })} data-testid="dna-required" />
                  Required for survey eligibility
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={editing.hidden} onCheckedChange={(v) => setEditing({ ...editing, hidden: v })} data-testid="dna-hidden" />
                  Hide from patron form
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => { setEditing(null); setAdding(false); }}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="dna-save">{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =====================================================================
// (2) Menu Permissions — admin picks what PM/Researcher can see
// =====================================================================
const MENU_LABELS = {
  "": "Dashboard",
  "health": "Oodel Health",
  "trackers": "Trackers",
  "questions": "Question Bank",
  "waves": "Waves",
  "sentiment": "Public Sentiment",
  "audience": "Audience Explorer",
  "customers": "Business Customers",
  "inbox": "PM Inbox",
  "waitlist": "Waitlist (Patrons)",
  "partners": "Partners & Vouchers",
  "raffle": "Raffle",
  "team": "Team",
  "sectors": "Sectors CMS",
  "rewards": "Rewards (legacy)",
  "editorial": "Editorial",
  "site-content": "Site Content",
  "email-templates": "Email Templates",
  "email-outbox": "Email Outbox",
  "users": "All Users",
  "my-account": "My Account",
  "profile-dna": "Patron Profile DNA",
  "menu-permissions": "Menu Permissions",
};

export function AdminMenuPermissions() {
  const [state, setState] = useState({ pm: [], researcher: [], all_slugs: [], pm_access: "full", researcher_access: "full" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/menu-permissions").then((r) => setState(r.data)).catch(() => toast.error("Failed to load"));
  }, []);

  const toggle = (role, slug) => {
    const cur = new Set(state[role]);
    if (cur.has(slug)) cur.delete(slug); else cur.add(slug);
    setState({ ...state, [role]: [...cur] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/menu-permissions", {
        pm: state.pm,
        researcher: state.researcher,
        pm_access: state.pm_access,
        researcher_access: state.researcher_access,
      });
      toast.success("Saved. PMs and researchers will see the updated menu on their next page load.");
    } catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const RoleCard = ({ role, label }) => {
    const accessKey = `${role}_access`;
    const access = state[accessKey] || "full";
    return (
      <div className="bg-white border-2 border-black p-5" data-testid={`role-card-${role}`}>
        <div className="overline mb-3">{label} CAN SEE:</div>

        {/* Access level toggle — Read-only vs Full access */}
        <div className="bg-neutral-900 text-white p-3 mb-4 flex items-center justify-between gap-3" data-testid={`access-${role}`}>
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-400">Access level (applies to all enabled menus)</div>
            <div className="text-xs text-neutral-400 mt-1">
              {access === "read_only" ? "Can view but cannot create / edit / delete." : "Full CRUD — create, edit, delete enabled."}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => setState({ ...state, [accessKey]: "read_only" })}
              className={`px-3 py-1 text-xs font-bold ${access === "read_only" ? 'bg-mango text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
              data-testid={`access-${role}-read`}
            >Read-only</button>
            <button
              onClick={() => setState({ ...state, [accessKey]: "full" })}
              className={`px-3 py-1 text-xs font-bold ${access === "full" ? 'bg-mango text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
              data-testid={`access-${role}-full`}
            >Full access</button>
          </div>
        </div>

        <div className="space-y-1">
          {(state.all_slugs || []).map((slug) => {
            const enabled = (state[role] || []).includes(slug);
            return (
              <div key={slug} className={`flex items-center justify-between gap-3 px-3 py-2 ${enabled ? 'bg-mango/10' : 'hover:bg-neutral-50'}`} data-testid={`perm-${role}-${slug || 'dashboard'}`}>
                <span className="text-sm">{MENU_LABELS[slug] || slug || "Dashboard"}</span>
                <Switch
                  checked={enabled}
                  onCheckedChange={() => toggle(role, slug)}
                  data-testid={`switch-${role}-${slug || 'dashboard'}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl" data-testid="admin-menu-permissions">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · ROLES & PERMISSIONS</div>
        <h1 className="text-4xl font-black tracking-tighter">Menu permissions</h1>
        <p className="text-sm text-neutral-500 mt-1">Toggle which sidebar menus appear for each non-admin role, and set the access level (Read-only vs Full CRUD). Admin always sees everything with full access.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RoleCard role="pm" label="PROJECT MANAGER" />
        <RoleCard role="researcher" label="RESEARCHER" />
      </div>

      <div className="mt-6">
        <Button onClick={save} disabled={saving} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="perm-save">{saving ? "Saving…" : "Save permissions"}</Button>
      </div>
    </div>
  );
}


// =====================================================================
// (3) Legal Pages Editor — Privacy Notice + Terms of Service
// =====================================================================
// Each "page" is a (title, body markdown) pair stored as two `site_content`
// keys. Admin picks the page from the top tab strip, edits both fields, and
// hits Save. Live preview is built into the MarkdownEditor.
const LEGAL_PAGES = [
  {
    slug: "privacy",
    label: "Privacy Notice",
    publicPath: "/privacy",
    titleKey: "legal_privacy_title",
    bodyKey: "legal_privacy_body",
    starter: "## Privacy Notice\n\noodel. takes your privacy seriously…\n\n### Information we collect\n- Account details (name, email, phone)\n- Demographic & lifestyle profile (used to weight responses for representative results)\n- Survey responses (always anonymised at the dataset level)\n\n### How we use it\nWe use your profile + responses to produce nationally-representative reports for our enterprise clients. Your individual identity is never shared.\n\n### Your rights\n- **Access** — request a copy of everything we hold about you\n- **Deletion** — close your account and we'll remove all PII within 30 days\n- **Correction** — update any field on your profile at any time\n\nContact: privacy@oodel.co",
  },
  {
    slug: "terms",
    label: "Terms of Service",
    publicPath: "/terms",
    titleKey: "legal_terms_title",
    bodyKey: "legal_terms_body",
    starter: "## Terms of Service\n\nBy creating an oodel. account you agree to the following…\n\n### Membership\nThis service is for residents of Pakistan aged 18+. Membership is free, but governed by these terms.\n\n### Honest participation\nYou agree to answer surveys truthfully. Repeated dishonest answers (detected via speed-trap and consistency checks) may result in account suspension.\n\n### Rewards & raffle\n- Points are non-cash and have no monetary value outside of the rewards catalogue / raffle system.\n- Raffle eligibility requires a minimum point threshold and clean account standing.\n- oodel. reserves the right to invalidate points obtained fraudulently.\n\n### Termination\nYou can close your account at any time. We may terminate accounts that breach these terms.\n\nContact: support@oodel.co",
  },
  {
    slug: "cookies",
    label: "Cookie Policy",
    publicPath: "/cookie-policy",
    titleKey: "legal_cookies_title",
    bodyKey: "legal_cookies_body",
    starter: "## Cookie Policy\n\noodel. uses cookies and similar technologies to keep the site working, understand how you use it, and (with your permission) personalise what you see. This page explains what we use, why, and how you can change your preferences.\n\n### What is a cookie?\nA cookie is a small text file a website stores on your device. Cookies are used by almost every modern site to remember your sign-in state, language preferences, and analytics — without them, basic features like staying logged in wouldn't work.\n\n### The three categories we use\n\n**1. Strictly necessary** *(always on — cannot be disabled)*\n- Keep you signed in across pages\n- Remember your language preference (English / Urdu)\n- Protect against cross-site request forgery (CSRF) and other abuse\n- Without these, the site cannot function. Legal basis: legitimate interest.\n\n**2. Analytics** *(opt-in)*\n- Help us understand which pages, surveys and articles are popular\n- Anonymous — never linked to your individual identity\n- Powers our editorial and product decisions\n- You can turn these off at any time from the [cookie banner](/) or the link in the footer.\n\n**3. Marketing & personalisation** *(opt-in)*\n- Show you survey invitations matched to your interests\n- Remember which articles you've already read\n- We **never** sell your data to third parties.\n\n### Cookies we set\n\n| Cookie name | Category | Purpose | Expiry |\n|---|---|---|---|\n| `pollpak_token` | Strictly necessary | Keeps you signed in | Session |\n| `oodel_anon_id` | Strictly necessary | Anonymous device ID for consent audit | 24 months |\n| `oodel_cookie_consent` | Strictly necessary | Remembers your cookie choice | 12 months |\n| `_ga`, `_gid` | Analytics | Google Analytics page metrics | 24 months / 24 hours |\n| `oodel_marketing` | Marketing | Personalisation state | 12 months |\n\nIf you reject the Analytics or Marketing categories, the cookies above are NOT set and the relevant scripts never load.\n\n### Changing your preferences\nClick **Cookie preferences** in the page footer to reopen the consent modal at any time. Your choices take effect immediately.\n\n### Audit & legal basis\n- We log every consent choice (timestamp, version, hashed IP and user-agent — never raw PII) for a **24-month** retention period as required by data-protection regulators.\n- Legal basis: **PECA 2016** (Pakistan), **GDPR Art. 6(1)(a)** (EU users), **PECR 2003** (UK users).\n- Consent version: bump triggers a fresh consent prompt for every visitor.\n\n### International transfers\nIf you access oodel. from outside Pakistan, your consent record may be processed on infrastructure outside your jurisdiction (e.g. our hosting provider's regional data centre). We use industry-standard transfer safeguards.\n\n### Your rights\nYou can at any time:\n- **Withdraw consent** by clicking *Reject all* or *Cookie preferences* in the banner / footer\n- **Request access** to the cookie-consent records we hold about you\n- **Request deletion** of those records (subject to legal retention requirements)\n\n### Cross-references\n- [Privacy Notice](/privacy) — how we handle survey responses and profile data\n- [Terms of Service](/terms) — the rules of the road for using oodel.\n\n### Contact\nQuestions about cookies or data protection? Email us at **privacy@oodel.co** — we respond within 5 working days.",
  },
];

export function AdminLegalPages() {
  const [active, setActive] = useState(LEGAL_PAGES[0].slug);
  const [content, setContent] = useState(getCachedSiteContent);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const cfg = LEGAL_PAGES.find((p) => p.slug === active);

  // Fetch full site_content once on mount; reload when admin switches pages.
  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data || {})).catch(() => {});
  }, []);

  // When tab changes, hydrate the editor with the saved values for that page.
  useEffect(() => {
    setTitle(content[cfg.titleKey] || cfg.label);
    setBody(content[cfg.bodyKey] || "");
    setDirty(false);
  }, [active, content, cfg]);

  const save = async () => {
    setSaving(true);
    try {
      // We write to two `site_content` keys in parallel.
      await Promise.all([
        api.put(`/admin/site-content/${cfg.titleKey}`, { value: title }),
        api.put(`/admin/site-content/${cfg.bodyKey}`, { value: body }),
        api.put(`/admin/site-content/${cfg.titleKey}_updated_at`, { value: new Date().toISOString() }),
      ]);
      // Refresh local snapshot so dirty-state goes back to false.
      const r = await api.get("/site-content");
      setContent(r.data || {});
      toast.success(`${cfg.label} published — live at ${cfg.publicPath}`);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const loadStarter = () => {
    if (body && body.trim().length > 0) {
      if (!window.confirm("Replace the current text with the starter template?")) return;
    }
    setBody(cfg.starter);
    setDirty(true);
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="admin-legal-pages">
      <div className="overline mb-1">CMS · LEGAL</div>
      <h1 className="text-3xl font-black tracking-tighter mb-1">Legal pages</h1>
      <p className="text-neutral-600 mb-6 text-sm">Write each policy in plain English. Use the toolbar to bold text, add headings, lists, links — exactly like writing in Google Docs. Published pages go live at <code>{cfg.publicPath}</code> instantly — no rebuild needed.</p>

      {/* Tab strip — switch between Privacy / Terms */}
      <div className="flex gap-1 border-b border-neutral-300 mb-6">
        {LEGAL_PAGES.map((p) => (
          <button
            key={p.slug}
            type="button"
            onClick={() => {
              if (dirty && !window.confirm("You have unsaved changes. Switch pages anyway?")) return;
              setActive(p.slug);
            }}
            data-testid={`legal-tab-${p.slug}`}
            className={`px-5 py-3 text-sm font-bold border-b-2 -mb-0.5 transition-colors ${
              active === p.slug ? "border-mango text-black" : "border-transparent text-neutral-500 hover:text-black"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        <div>
          <Label className="overline text-xs">Page title</Label>
          <Input
            data-testid="legal-title"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            className="rounded-none text-xl font-bold"
            placeholder={cfg.label}
          />
        </div>

        <div>
          <div className="flex items-end justify-between mb-1">
            <Label className="overline text-xs">Page body — supports bold, italic, headings, lists, links, quotes</Label>
            <button type="button" onClick={loadStarter} className="text-xs text-neutral-500 hover:text-black underline" data-testid="legal-load-starter">
              Load starter template
            </button>
          </div>
          <MarkdownEditor
            value={body}
            onChange={(v) => { setBody(v); setDirty(true); }}
            minHeight="h-[500px]"
            testid="legal-md"
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <a href={cfg.publicPath} target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-500 hover:text-black underline" data-testid="legal-preview-live">
            Preview live page →
          </a>
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-amber-700 font-bold">● Unsaved changes</span>}
            <Button onClick={save} disabled={saving || !dirty} className="bg-black hover:bg-neutral-800 text-white rounded-none" data-testid="legal-save">
              {saving ? "Publishing…" : "Save & publish"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
